import { format as formatDate, isValid as isValidDate, parse as parseDate } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const VALUE_FORMAT = "yyyy-MM-dd'T'HH:mm";
const DATE_FORMAT = "yyyy-MM-dd";
/** A prefilled day whose time is still missing. */
const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

interface DateTimePickerProps {
  id?: string;
  /**
   * "yyyy-MM-dd'T'HH:mm" (same shape as a native datetime-local input), or "" when empty.
   * A bare "yyyy-MM-dd" is a day waiting for its time — how a form prefills a sibling field.
   */
  value?: string;
  onChange: (value: string) => void;
  /**
   * Day the field falls back to while empty: the month the calendar opens on, and the date
   * used when only a time is typed. Lets a form chain its fields — type the time, the day
   * comes from the one already filled — instead of defaulting to today behind the user's back.
   */
  defaultDate?: Date | null;
}

/** Popover date picker + time input — replaces the raw native `datetime-local` input for better cross-browser UX. */
export function DateTimePicker({ id, value, onChange, defaultDate }: DateTimePickerProps) {
  const [open, setOpen] = useState(false);

  const dateOnly = DATE_ONLY_REGEX.test(value ?? "");
  const parsed = value ? parseDate(value, dateOnly ? DATE_FORMAT : VALUE_FORMAT, new Date()) : undefined;
  const selectedDate = parsed && isValidDate(parsed) ? parsed : undefined;
  const timeValue = selectedDate && !dateOnly ? formatDate(selectedDate, "HH:mm") : "";

  function commit(date: Date | undefined, time: string) {
    if (!date) {
      onChange("");
      return;
    }
    // No time yet: keep the day alone rather than inventing midnight, which would read as
    // a real schedule and could be submitted as one.
    if (!time) {
      onChange(formatDate(date, DATE_FORMAT));
      return;
    }
    const [hours, minutes] = time.split(":").map(Number);
    const next = new Date(date);
    next.setHours(hours || 0, minutes || 0, 0, 0);
    onChange(formatDate(next, VALUE_FORMAT));
  }

  return (
    <div className="flex gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={<Button id={id} variant="outline" className="flex-1 justify-start font-normal" />}
        >
          <CalendarIcon data-icon="inline-start" />
          {selectedDate ? formatDate(selectedDate, "d MMM yyyy", { locale: fr }) : "Sélectionner une date"}
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            locale={fr}
            defaultMonth={selectedDate ?? defaultDate ?? undefined}
            selected={selectedDate}
            onSelect={(date) => {
              commit(date, timeValue);
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
      <Input
        type="time"
        className="w-28"
        value={timeValue}
        onChange={(event) => commit(selectedDate ?? defaultDate ?? new Date(), event.target.value)}
      />
    </div>
  );
}
